import { CostFactorInsightCards } from '@/components';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import {
  OnboardingLayout,
  useSlideSetup,
  getFirstCarePhasePage,
  AnimationToggle,
  useAnimationPreferences,
  CostFactorsAnimation,
} from '@/features/client-onboarding';
import { OnboardingResultsAreIn } from '@/pages/OnboardingResultsAreIn';
import { OnboardingFundingSources } from '@/pages/OnboardingFundingSources';
import { selectClient, selectSession, selectFeatureFlags } from '@/model';
import { useSelector } from 'react-redux';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { possessive, SessionType } from '@shared';

export function OnboardingCostFactors({ clientId }: ClientIdProps) {
  const { flags } = useFeatureFlags();
  const client = useSelector(selectClient)!;
  const { sessionType } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const { animationsEnabled } = useAnimationPreferences();
  const isClientSession = sessionType === SessionType.client;
  const isD2C = isClientSession && !!featureFlags.d2cMode;
  const isGenworthMode = !!featureFlags.genworthMode;

  const {
    clientFirstName,
    allPhaseCosts: { allPhaseInflatedProfessionalShareCost },
    intakeSurvey: { clientHasStartedLtc },
  } = client;

  const showAnimations = flags.animatedSlides && animationsEnabled;

  useSlideSetup({
    slidePage: OnboardingCostFactors,
    nextButtonLabel: 'Personalize Care Plan',
    nextSlidePage: getFirstCarePhasePage(client),
    previousSlidePage: clientHasStartedLtc ? OnboardingResultsAreIn : undefined,
    secondaryNextButtonLabel: isD2C || isGenworthMode ? undefined : 'Skip to Funding Plan',
    secondaryNextSlidePage: isD2C || isGenworthMode ? undefined : OnboardingFundingSources,
  });

  return (
    <OnboardingLayout>
      <div className="mx-auto flex w-full max-w-3xl flex-col py-6 md:py-16">
        <div className="flex w-full items-center justify-between gap-6">
          <h1
            data-testid="cost-factors-header"
            className="text-balance text-2xl font-bold text-gray-950 md:text-4xl"
          >
            {possessive(clientFirstName)} estimated{clientHasStartedLtc ? ' remaining' : ''} long-term
            care costs
          </h1>
          {flags.animatedSlides && (
            <div className="flex shrink-0 items-center gap-2">
              <AnimationToggle />
            </div>
          )}
        </div>
        {showAnimations ? (
          <CostFactorsAnimation />
        ) : (
          <>
            <div
              className="bg-primary/5 border-primary/20 mt-6 flex items-center justify-between rounded-xl border px-6 py-5"
              data-testid="cost-factors-total"
            >
              <div>
                <p className="text-sm font-medium text-gray-600">Estimated total costs</p>
                <p className="text-muted-foreground text-xs">*without a funding plan</p>
              </div>
              <p
                className="text-primary-dark text-2xl font-bold md:text-3xl"
                data-testid="cost-factors-total-value"
              >
                <LtcCostDisplay value={allPhaseInflatedProfessionalShareCost} />
              </p>
            </div>
            <div className="mt-6">
              <CostFactorInsightCards />
            </div>
          </>
        )}
        <div className="mt-6 rounded-lg bg-gray-50 p-4 text-xs leading-relaxed text-gray-500">
          These projections are estimates based on models trained on other families' LTC journeys.
          Actual costs and timing may differ. This information is for educational purposes only.
        </div>
      </div>
    </OnboardingLayout>
  );
}
